/**
 * Templates Page — Browse and manage project templates
 */

import { useState } from 'react'
import {
  Search,
  FileText,
  Users,
  Target,
  Map,
  BarChart3,
  Layers,
  BookOpen,
  MessageSquare,
  Route,
  Film,
  Code,
  Package,
  LayoutDashboard,
  Star,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTemplates } from '@/hooks/use-templates'
import type { Template } from '@audra-flow/types'
import type { LucideIcon } from 'lucide-react'

const TYPE_ICONS: Record<string, LucideIcon> = {
  GOAL: Target,
  INTERVIEW: MessageSquare,
  COMPETITOR_ANALYSIS: BarChart3,
  PERSONA: Users,
  USE_CASE: Layers,
  JOURNEY: Route,
  SERVICE_MAP: Map,
  STORYBOARD: Film,
  BASE_SPEC: FileText,
  TECHNICAL_SPEC: Code,
  STORY: BookOpen,
  RELEASE: Package,
  EVIDENCE: FileText,
  PROJECT_OVERVIEW: LayoutDashboard,
}

const TYPE_LABELS: Record<string, string> = {
  GOAL: 'Goals',
  INTERVIEW: 'Interview',
  COMPETITOR_ANALYSIS: 'Competitor Analysis',
  PERSONA: 'Persona',
  USE_CASE: 'Use Case',
  JOURNEY: 'Journey',
  SERVICE_MAP: 'Service Map',
  STORYBOARD: 'Storyboard',
  BASE_SPEC: 'Base Spec',
  TECHNICAL_SPEC: 'Technical Spec',
  STORY: 'Story',
  RELEASE: 'Release',
  EVIDENCE: 'Evidence',
  PROJECT_OVERVIEW: 'Project Overview',
}

function getCategoryColor(category: string) {
  switch (category.toLowerCase()) {
    case 'discovery':
      return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'definition':
      return 'bg-purple-50 text-purple-700 border-purple-200'
    case 'design':
      return 'bg-pink-50 text-pink-700 border-pink-200'
    case 'delivery':
      return 'bg-green-50 text-green-700 border-green-200'
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200'
  }
}

function getCategoryIconBg(category: string) {
  switch (category.toLowerCase()) {
    case 'discovery':
      return 'bg-blue-100 text-blue-700'
    case 'definition':
      return 'bg-purple-100 text-purple-700'
    case 'design':
      return 'bg-pink-100 text-pink-700'
    case 'delivery':
      return 'bg-green-100 text-green-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'ACTIVE':
      return 'bg-emerald-50 text-emerald-700'
    case 'DRAFT':
      return 'bg-yellow-50 text-yellow-700'
    case 'IN_REVIEW':
      return 'bg-blue-50 text-blue-700'
    case 'DEPRECATED':
      return 'bg-gray-100 text-gray-500'
    default:
      return 'bg-gray-50 text-gray-700'
  }
}

function TemplateCard({ template }: { template: Template }) {
  const Icon = TYPE_ICONS[template.type] || FileText

  return (
    <div className="bg-white border border-gray-200 rounded-[14px] p-5 hover:border-gray-300 hover:shadow-sm transition-all">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0',
          getCategoryIconBg(template.category)
        )}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-medium text-gray-900 truncate">
              {template.name}
            </h3>
            <span className="text-xs text-gray-400 flex-shrink-0">
              v{template.activeVersion}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {TYPE_LABELS[template.type] || template.type}
          </p>
        </div>
      </div>

      {/* Description */}
      {template.description && (
        <p className="text-sm text-gray-600 mt-3 line-clamp-2">
          {template.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 mt-4">
        <span className={cn(
          'px-2 py-0.5 rounded-full text-xs font-medium',
          getCategoryColor(template.category)
        )}>
          {template.category.charAt(0).toUpperCase() + template.category.slice(1)}
        </span>
        <span className={cn(
          'px-2 py-0.5 rounded-full text-xs font-medium',
          getStatusColor(template.status)
        )}>
          {template.status}
        </span>
        {template.isDefault && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
            <Star className="w-3 h-3" />
            Default
          </span>
        )}
      </div>
    </div>
  )
}

export default function TemplatesPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)

  const { data, isLoading, isError } = useTemplates({
    pageSize: 50,
    ...(selectedStatus ? { status: selectedStatus } : {}),
    ...(selectedCategory ? { category: selectedCategory.toLowerCase() } : {}),
    ...(searchQuery ? { search: searchQuery } : {}),
  })

  const templates = data?.templates || []
  const categories = ['All', 'Discovery', 'Definition', 'Design', 'Delivery']
  const statuses = ['All', 'ACTIVE', 'DRAFT', 'IN_REVIEW', 'DEPRECATED']

  return (
    <div className="px-6 pt-8 pb-12 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-xl font-medium text-gray-900 mb-1">Templates</h1>
        <p className="text-base text-gray-500">
          Browse templates used by AI generation across your projects
        </p>
      </div>

      {/* Search + Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-[10px] text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>

        <div className="flex items-center gap-6">
          {/* Category tabs */}
          <div className="flex items-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat === 'All' ? null : cat)}
                className={cn(
                  'px-3 py-1.5 rounded-[8px] text-sm font-medium transition-colors',
                  (selectedCategory === cat || (!selectedCategory && cat === 'All'))
                    ? 'bg-brand-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-500 mr-1">Status:</span>
            {statuses.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status === 'All' ? null : status)}
                className={cn(
                  'px-2.5 py-1 rounded-[6px] text-xs font-medium transition-colors',
                  (selectedStatus === status || (!selectedStatus && status === 'All'))
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {status === 'All' ? 'All' : status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Template Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          <span className="ml-2 text-gray-500">Loading templates...</span>
        </div>
      ) : isError ? (
        <div className="text-center py-20">
          <FileText className="w-12 h-12 text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load templates</h3>
          <p className="text-gray-500">Check that the backend is running and try again.</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-20">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-500">
            {searchQuery || selectedCategory || selectedStatus
              ? 'Try adjusting your filters.'
              : 'No templates have been created yet.'
            }
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-400 mb-4">
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {templates.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
